import type { Brand } from "@adora/shared/types/brand";
import type { Creator, FilterValues } from "../../types/creative-eval";
import { ACTION_OPTIONS, MONTH_OPTIONS } from "./constants";

interface FilterBarProps {
  open: boolean;
  brands: Brand[] | undefined;
  isLoadingBrands: boolean;
  creators: Creator[] | undefined;
  isLoadingCreators: boolean;
  filters: FilterValues;
  isLoading: boolean;
  onFilterChange: (patch: Partial<FilterValues>) => void;
  onActionToggle: (action: string) => void;
  onFetch: () => void;
  onClose: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  open,
  brands,
  isLoadingBrands,
  creators,
  isLoadingCreators,
  filters,
  isLoading,
  onFilterChange,
  onActionToggle,
  onFetch,
  onClose
}) => {
  return (
    <div className={`new-eval-panel ${open ? "open" : ""}`}>
      <div className="new-eval-card">
        <div className="new-eval-card-header">
          <h3>Filters</h3>
          <button className="new-eval-close" onClick={onClose} type="button" aria-label="Close filters panel">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="new-eval-fields">
          <div className="field">
            <label htmlFor="ce-brand">Brand</label>
            <div className="select-loading-wrap">
              <select
                id="ce-brand"
                value={filters.brandId || ""}
                onChange={(e) => onFilterChange({ brandId: Number(e.target.value) || null, userId: "" })}
                disabled={isLoadingBrands}
              >
                <option value="">{isLoadingBrands ? "Loading brands..." : "Select a brand"}</option>
                {brands?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} (ID: {b.id})
                  </option>
                ))}
              </select>
              {isLoadingBrands && <span className="field-loading-spinner" aria-label="Loading brands" />}
            </div>
          </div>

          <div className="field">
            <label htmlFor="ce-user">User (optional)</label>
            <select
              id="ce-user"
              value={filters.userId}
              onChange={(e) => onFilterChange({ userId: e.target.value })}
              disabled={!filters.brandId || isLoadingCreators}
            >
              <option value="">{isLoadingCreators && filters.brandId ? "Loading users..." : "All users"}</option>
              {creators?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          className={`filters-toggle ${filters.filtersOpen ? "expanded" : ""}`}
          onClick={() => onFilterChange({ filtersOpen: !filters.filtersOpen })}
          type="button"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
          More Filters
        </button>

        <div className={`filters-extra ${filters.filtersOpen ? "open" : ""}`}>
          <div className="filters-extra-inner">
            <div className="filters-section-label">Image Filters</div>
            <div className="field">
              <label htmlFor="ce-image-id">Specific Image ID</label>
              <input
                id="ce-image-id"
                type="number"
                placeholder="Leave blank for recent images"
                value={filters.imageId}
                onChange={(e) => onFilterChange({ imageId: e.target.value })}
              />
            </div>

            <div className="field">
              <div className="field-label">Actions to Include</div>
              <div className="checkbox-row">
                {ACTION_OPTIONS.map((action) => (
                  <label key={action.value}>
                    <input
                      type="checkbox"
                      checked={filters.selectedActions.includes(action.value)}
                      onChange={() => onActionToggle(action.value)}
                    />{" "}
                    {action.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="field">
              <div className="checkbox-row">
                <label>
                  <input
                    type="checkbox"
                    checked={filters.includeEvaluated}
                    onChange={(e) => onFilterChange({ includeEvaluated: e.target.checked })}
                  />{" "}
                  Show Prior Evaluated
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={filters.randomize}
                    onChange={(e) => onFilterChange({ randomize: e.target.checked })}
                  />{" "}
                  Randomize
                </label>
              </div>
            </div>

            <div className="filters-section-label">Time Range</div>
            <div className="field">
              <label htmlFor="ce-months">Months Back</label>
              <select
                id="ce-months"
                value={filters.monthsBack}
                onChange={(e) => onFilterChange({ monthsBack: Number(e.target.value) })}
              >
                {MONTH_OPTIONS.map((months) => (
                  <option key={months} value={months}>
                    Last {months} {months === 1 ? "month" : "months"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="ce-date-start">Custom Start Date</label>
              <input
                id="ce-date-start"
                type="date"
                value={filters.dateStart}
                onChange={(e) => onFilterChange({ dateStart: e.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="ce-date-end">Custom End Date</label>
              <input
                id="ce-date-end"
                type="date"
                value={filters.dateEnd}
                onChange={(e) => onFilterChange({ dateEnd: e.target.value })}
              />
            </div>

            <div className="filters-section-label">Batch Size</div>
            <div className="field">
              <label htmlFor="ce-batch">Images Per Batch</label>
              <input
                id="ce-batch"
                type="number"
                value={filters.batchSize}
                min={1}
                max={50}
                onChange={(e) => onFilterChange({ batchSize: Number(e.target.value) })}
              />
            </div>
          </div>
        </div>

        <div className="new-eval-actions">
          <button
            className="primary-btn accent-primary"
            onClick={onFetch}
            disabled={!filters.brandId || isLoading}
            type="button"
          >
            {isLoading ? "Loading..." : "Apply Filters"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
